import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'

interface Props {
  open: boolean
  onClose: () => void
  title?: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

const SIZES: Record<NonNullable<Props['size']>, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-xl',
  lg: 'sm:max-w-3xl',
  xl: 'sm:max-w-5xl',
}

/**
 * Modal responsiva:
 *  - sm (≥640px): modal centralizada com max-width controlado
 *  - <sm: full-screen (slide-up) — comportamento nativo mobile
 */
export function Modal({ open, onClose, title, description, children, footer, size = 'md' }: Props) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/40 sm:backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={cn(
          'w-full bg-white ring-1 ring-gray-300 flex flex-col',
          // Mobile: full-screen, slide-up. Desktop: modal centralizada com max-w
          'h-dvh sm:h-auto sm:max-h-[92vh] sm:rounded-md rounded-none',
          SIZES[size],
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-4 sm:px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="min-w-0">
            {title       && <h3 className="text-base font-semibold text-gray-800 break-words">{title}</h3>}
            {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
          </div>
          <button
            onClick={onClose}
            className="cursor-pointer p-2 -mr-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 shrink-0"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-5">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 px-4 sm:px-5 py-3 border-t border-gray-200 bg-gray-50/60 sm:rounded-b-md shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
